import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Form, FormGroup, FormLabel, FormControl, InputGroup, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import MainLogo from "../../../images/main-logo.svg";
import LoginSlider from './LoginSlider/LoginSlider.tsx';
import apiConfig from '../apiConfig.ts';
import { API_ENDPOINTS } from '../../utils.ts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthHelper } from '../../../utils/authHelper';
import "./Login.scss";

interface LoginProps {
  onLoginSuccess?: () => void;
}

// Validation schema
const loginSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
  
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
});

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // Load saved credentials on component mount
  useEffect(() => {
    // Check if user already has a valid token and should be redirected
    if (AuthHelper.hasValidToken()) {
      navigate('/dashboard');
      return;
    }

    // Load remember me data
    const rememberMeData = AuthHelper.getRememberMeData();
    
    if (rememberMeData) {
      // Check if remember me session is not too old (30 days)
      if (!AuthHelper.isRememberMeExpired(30)) {
        formik.setFieldValue('email', rememberMeData.email);
        setRememberMe(true);
      } else {
        // Clear expired remember me data
        AuthHelper.clearRememberMeData();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const response = await axios.post(`${apiConfig.apiBaseURL}${API_ENDPOINTS.LOGIN}`,
          {
            email: values.email.trim(),
            password: values.password,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === "Success") {
          // Store auth token using AuthHelper
          if (response.data.token) {
            AuthHelper.saveAuthToken(response.data.token);
          }

          // Handle remember me functionality using AuthHelper
          AuthHelper.saveRememberMeData(values.email, rememberMe);

          toast.success(response.data.message || 'Login successful! Welcome back.');
          
          // Call success callback if provided
          if (onLoginSuccess) {
            onLoginSuccess();
          }
          
          // Navigate to admin dashboard
          navigate('/dashboard');
        } else {
          toast.error(response.data.message || 'Login failed. Please try again.');
        }
      } catch (error: any) {
        
        // Handle specific error cases
        if (error.response?.status === 404) {
          toast.error('Admin account not found. Please check your credentials.');
        } else if (error.response?.status === 401) {
          toast.error(error.response?.data?.message || 'Invalid credentials. Please try again.');
        } else {
          toast.error(error.response?.data?.message || 'Login failed. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Handle form submission with validation
  const handleValidateAndSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all the required fields correctly");
      formik.setTouched({ email: true, password: true });
      return;
    }
    formik.handleSubmit();
  };

  // Prevent space key in email and password fields
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
    }
  };

  // Sanitize email input
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedEmail = e.target.value.toLowerCase().trim();
    formik.setFieldValue("email", sanitizedEmail);
  };

  return (
    <section className='login-screen'>
      <div className='row'>
        <div className='login-screen-left col-md-6'>
          <LoginSlider />
        </div>

        <div className='login-screen-right col-md-6'>
          <div className='login-screen-right-in'>
            <div className='middle-block'>
              <figure className='main-logo'>
                <img src={MainLogo} alt='Logo' />
              </figure>
              <h3>Admin Login</h3>
              
              <Form onSubmit={formik.handleSubmit}>
                {/* Email Field */}
                <FormGroup className="form-group">
                  <FormLabel>Email *</FormLabel>
                  <FormControl 
                    autoFocus
                    type="email" 
                    name="email"
                    placeholder="Enter admin email address" 
                    value={formik.values.email}
                    onChange={handleEmailChange}
                    // onBlur={formik.handleBlur}
                    className="form-control p-3"
                    autoComplete="email"
                    onKeyDown={handleKeyDown}
                    disabled={formik.isSubmitting}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <small
                      style={{
                        color: '#dc3545',
                        display: 'block',
                        marginTop: '4px',
                        fontSize: '0.875rem'
                      }}
                    >
                      {formik.errors.email}
                    </small>
                  )}
                </FormGroup>

                {/* Password Field */}
                <FormGroup className='form-group password-field'>
                  <FormLabel>Password *</FormLabel>
                  <InputGroup>
                    <FormControl
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Enter password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      // onBlur={formik.handleBlur}
                      className="form-control p-3"
                      autoComplete="current-password"
                      onKeyDown={handleKeyDown}
                      disabled={formik.isSubmitting}
                    />
                    <InputGroup.Text
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      {showPassword ? <FiEye size={20} /> : <FiEyeOff size={20} />}
                    </InputGroup.Text>
                  </InputGroup>
                  {formik.touched.password && formik.errors.password && (
                    <small
                      style={{
                        color: '#dc3545',
                        display: 'block',
                        marginTop: '4px',
                        fontSize: '0.875rem'
                      }}
                    >
                      {formik.errors.password}
                    </small>
                  )}
                </FormGroup>

                {/* Remember Me Checkbox */}
                {/* <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="rememberMe"
                    label="Remember me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={formik.isSubmitting}
                  />
                </Form.Group> */}

                {/* Login Button */}
                <Button
                  className='main-bttn'
                  type="submit"
                  onClick={handleValidateAndSubmit}
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Logging in...' : 'Login'}
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
